import { memo, useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  ViewfinderCircleIcon,
  XCircleIcon
} from '@heroicons/react/20/solid';
import { Panel } from '../../../components/panels/Panel';
import { useSocket } from '../../../contexts/SocketContext';
import {
  SCAN_STATE_CURRENT,
  SCAN_STATE_OTHER,
  SCAN_STATE_PROCESS_BASE,
  SCAN_STATE_RACK_NOT_SCANNED,
  SCAN_STATE_WAITING
} from '../../../services/live-interface/dior/dior-interface-service';
import '../../../styles/dior/digital_twin.scss';

const mergeScanProcess = (previous, update) => {
  const next = { ...previous };

  // Overwrite received fields while preserving any fields omitted by the socket message.
  Object.entries(update).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const oldValue = next[key] && typeof next[key] === 'object' && !Array.isArray(next[key]) ? next[key] : {};
      next[key] = mergeScanProcess(oldValue, value);
    } else {
      next[key] = value;
    }
  });

  return next;
};

const SCAN_STEPS = [
  { key: 'rack_detection', title: 'Detecção do rack' },
  { key: 'rack_placement', title: 'Posicionamento do rack' },
  { key: 'tag_detection', title: 'Detecção da etiqueta' },
  { key: 'tag_read', title: 'Leitura da etiqueta' },
  { key: 'start_authorized', title: 'Início autorizado' }
];

const STEP_APPEARANCES = {
  idle: { label: 'Inativo', Icon: ClockIcon, badgeType: 'gray', color: 'text-perception-gray-700', background: 'bg-perception-black-500/70' },
  validated: { label: 'Validado', Icon: CheckCircleIcon, badgeType: 'green', color: 'text-perception-success-300', background: 'bg-perception-success-600/30' },
  error: { label: 'Erro', Icon: XCircleIcon, badgeType: 'red', color: 'text-perception-error-500', background: 'bg-perception-error-500/30' },
  wait: { label: 'Aguardando', Icon: ArrowPathIcon, badgeType: 'blue', color: 'text-perception-blue', background: 'bg-perception-blue/30' },
  not_applicable: { label: 'Não aplicável', Icon: MinusCircleIcon, badgeType: 'gray', color: 'text-perception-gray-500', background: 'bg-perception-black-500/70' }
};

const badgeTypes = {
  gray: 'bg-gray-400/10 text-gray-400 ring-gray-400/20',
  red: 'bg-red-400/10 text-red-400 ring-red-400/20',
  yellow: 'bg-yellow-400/10 text-yellow-500 ring-yellow-400/20',
  green: 'bg-green-500/10 text-green-400 ring-green-500/20',
  blue: 'bg-blue-400/10 text-blue-400 ring-blue-400/30',
  indigo: 'bg-indigo-400/10 text-indigo-400 ring-indigo-400/30',
  purple: 'bg-purple-400/10 text-purple-400 ring-purple-400/30',
  pink: 'bg-pink-400/10 text-pink-400 ring-pink-400/20',
  black: 'bg-gray-800/10 text-gray-800 ring-gray-800/20',
  perception: 'bg-perception-blue/10 text-perception-blue ring-perception-blue/20'
};

const Badge = ({ text, type }) => {
  const badgeStyle = badgeTypes[type] || badgeTypes.gray;
  return (
    <span className={`inline-flex max-w-full min-w-0 items-center rounded-md px-2 py-1 text-base font-medium ring-1 ring-inset ${badgeStyle}`}>
      <span className="block min-w-0 truncate">{text}</span>
    </span>
  );
};

const capitalizeFirst = (value) => {
  const text = String(value ?? '').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const getRackColorDotClass = (value) => {
  const color = String(value ?? '').toLowerCase();
  if (/blue|azul/.test(color)) return 'bg-sky-600/75 ring-sky-500/25';
  if (/green|verde/.test(color)) return 'bg-emerald-600/75 ring-emerald-500/25';
  if (/gray|grey|cinza/.test(color)) return 'bg-gray-500/75 ring-gray-400/25';
  return 'bg-gray-500/75 ring-gray-400/25';
};

const getStepAppearance = (stepKey, status) => {
  if (status === 1) return STEP_APPEARANCES.validated;
  if (status === 2) return stepKey === 'start_authorized' ? STEP_APPEARANCES.validated : STEP_APPEARANCES.error;
  if (status === 3) return STEP_APPEARANCES.wait;
  if (status === 4) return STEP_APPEARANCES.not_applicable;
  return STEP_APPEARANCES.idle;
};

const getStepDetail = (stepKey, step) => {
  if (!step || step.status === 0) return null;
  if (stepKey === 'rack_detection') return step.rack_color;
  if (stepKey === 'rack_placement') return step.message;
  if (stepKey === 'tag_read') return step.process_name;
  return null;
};

const getBBoxStyle = (bbox) => {
  if (!Array.isArray(bbox) || bbox.length !== 4) return null;
  const [left, top, right, bottom] = bbox.map(Number);
  if (![left, top, right, bottom].every(Number.isFinite) || right <= left || bottom <= top) return null;

  // Coordinates intentionally remain unclamped so the rack can extend beyond the scan area.
  return { left: `${left * 100}%`, top: `${top * 100}%`, width: `${(right - left) * 100}%`, height: `${(bottom - top) * 100}%` };
};

const DiorScanDigitalTwin = ({ steps, positions }) => {
  const viewportRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(0);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const updateCanvasSize = () => {
      const { width, height } = viewport.getBoundingClientRect();
      const nextWidth = Math.max(0, Math.min(width * 0.94, height * 0.8 * (16 / 9)));
      setCanvasWidth((previous) => previous === nextWidth ? previous : nextWidth);
    };

    updateCanvasSize();
    const observer = new ResizeObserver(updateCanvasSize);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  const rackVisible = (steps?.rack_detection?.status ?? 0) !== 0;
  const tagVisible = (steps?.tag_detection?.status ?? 0) !== 0;
  const rackBounds = getBBoxStyle(positions?.rack?.bbox);
  const rackStyle = rackVisible ? rackBounds : null;
  const tagStyle = tagVisible ? getBBoxStyle(positions?.process_tag?.bbox) : null;
  const missingRackPosition = (steps?.rack_placement?.status ?? 0) !== 0 && !rackBounds;
  const missingTagPosition = tagVisible && steps?.tag_detection?.status !== 4 && !tagStyle;

  return (
    <div ref={viewportRef} className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden bg-grid">
      <div className="relative flex-shrink-0" style={{ width: canvasWidth, aspectRatio: '16 / 9', overflow: 'visible' }}>
        <div className="absolute inset-0 border-2 border-dashed border-perception-gray-700/70 rounded-md pointer-events-none" style={{ boxShadow: '0 0 0 100vmax rgba(255, 255, 255, 0.08)' }} />
        <div className="absolute left-1/2 top-0 h-full border-l border-white/10 pointer-events-none" />
        <div className="absolute top-1/2 left-0 w-full border-t border-white/10 pointer-events-none" />
        {
          rackStyle &&
          <div className="absolute rounded-xl border-[6px] border-[#568a37] bg-[#568a37]/10 shadow-[0_0_12px_rgba(86,138,55,0.3)] transition-all duration-100 ease-linear" style={rackStyle}>
            <svg className="absolute left-1/2 -top-[63px] -translate-x-1/2 w-[98px] h-[66px] overflow-visible pointer-events-none" viewBox="0 0 98 66" fill="none" aria-hidden="true">
              <path d="M49 46 V63" stroke="#568a37" strokeWidth="7" strokeLinecap="round" />
              <path d="M9 5 H89 L72 46 H26 Z" fill="#171b1f" stroke="#afb6bf" strokeWidth="4" strokeLinejoin="round" />
              <path d="M49 17 V33" stroke="#afb6bf" strokeWidth="4" strokeLinecap="round" />
            </svg>
          </div>
        }
        {
          tagStyle &&
          <div className="absolute rounded-sm border-4 border-[#f4c51d] bg-[#f4c51d]/20 shadow-[0_0_12px_rgba(244,197,29,0.3)] transition-all duration-100 ease-linear" style={tagStyle} />
        }
      </div>
      {
        (missingRackPosition || missingTagPosition) &&
        <div className="absolute bottom-2 left-3 right-3 flex justify-center pointer-events-none">
          <div className="text-lg text-perception-error-500 bg-perception-black-800/90 rounded-md px-3 py-1">
            {missingRackPosition ? 'Posição do rack indisponível' : 'Posição da etiqueta indisponível'}
          </div>
        </div>
      }
    </div>
  );
};

const getScanFeedback = (scanState, steps, processes) => {
  const tagRead = steps?.tag_read;
  const placement = steps?.rack_placement;
  const processName = tagRead?.process_name ?? processes?.[scanState - SCAN_STATE_PROCESS_BASE]?.name;

  if (scanState === SCAN_STATE_WAITING) return { type: 'wait', title: 'Aguarde antes de escanear o próximo rack...' };
  if (scanState === SCAN_STATE_RACK_NOT_SCANNED) return { type: 'error', title: 'Novo rack não escaneado!' };
  if (scanState === SCAN_STATE_OTHER) return { type: 'error', title: 'Processo não cadastrado identificado' };
  if (tagRead?.status === 1 && tagRead?.process_name) return { type: 'validated', title: `Processo ${tagRead.process_name} identificado` };
  if (scanState >= SCAN_STATE_PROCESS_BASE) {
    if (processes?.[scanState - SCAN_STATE_PROCESS_BASE]?.commentary?.toLowerCase() === 'rework') return { type: 'validated', title: 'Processo de retrabalho identificado' };
    return { type: 'validated', title: `Processo ${processName ?? '(Processo desconhecido)'} identificado` };
  }
  if (placement?.message && placement?.status !== 0) return { type: placement?.status === 2 ? 'error' : 'wait', title: placement.message };
  if (tagRead?.status === 2) return { type: 'error', title: 'Não foi possível ler a etiqueta do processo' };
  if (steps?.tag_detection?.status === 2) return { type: 'error', title: 'Etiqueta do processo não encontrada' };
  if (steps?.rack_detection?.status === 2) return { type: 'error', title: 'Não foi possível confirmar a cor do rack' };
  if (tagRead?.status === 3) return { type: 'wait', title: 'Lendo a etiqueta do processo...' };
  if (steps?.tag_detection?.status === 3) return { type: 'wait', title: 'Detectando a etiqueta do processo...' };
  if (placement?.status === 3) return { type: 'wait', title: 'Verificando o posicionamento do rack...' };
  if (steps?.rack_detection?.status === 3) return { type: 'wait', title: 'Detectando o rack...' };
  if (steps?.start_authorized?.status === 1) return { type: 'validated', title: 'Pronto para iniciar' };
  if (scanState === SCAN_STATE_CURRENT || steps?.rack_detection?.status === 1) return { type: 'scanning', title: 'Escaneando...' };
  return { type: 'idle', title: 'Aguardando um rack para escanear...' };
};

const DiorScanFeedback = ({ scanState, steps, processes, tagImage }) => {
  const feedback = getScanFeedback(scanState, steps, processes);
  const tagImageSrc = typeof tagImage === 'string' ? tagImage : tagImage?.base64;
  const appearance = feedback.type === 'validated' ? STEP_APPEARANCES.validated : feedback.type === 'error' ? STEP_APPEARANCES.error : feedback.type === 'wait' || feedback.type === 'scanning' ? STEP_APPEARANCES.wait : STEP_APPEARANCES.idle;
  const Icon = feedback.type === 'error' ? ExclamationTriangleIcon : feedback.type === 'validated' ? CheckCircleIcon : feedback.type === 'wait' ? ArrowPathIcon : ViewfinderCircleIcon;

  return (
    <div className={`flex flex-shrink-0 min-h-[86px] items-center justify-center gap-4 px-5 py-3 border-t border-perception-black-500 bg-perception-black-800 ${appearance.color}`}>
      <Icon className={`size-10 flex-shrink-0 ${feedback.type === 'wait' ? 'animate-spin' : ''}`} />
      <div className="min-w-0 text-3xl font-medium text-center break-words">{feedback.title}</div>
      {steps?.tag_read?.status === 2 && tagImageSrc && <img src={tagImageSrc} alt="Etiqueta ilegível" className="max-h-20 max-w-28 flex-shrink-0 rounded-md object-contain border border-perception-error-500/40" />}
    </div>
  );
};

const DiorScanSteps = memo(({ steps }) => {
  return (
    <div className="w-[45%] min-w-0 flex flex-col border-l border-perception-black-500 bg-perception-black-800">
      <div className="flex-1 min-h-0 flex flex-col gap-2 p-3 overflow-y-auto">
        {
          SCAN_STEPS.map(({ key, title }, index) => {
            const step = steps?.[key] ?? {};
            const appearance = getStepAppearance(key, step.status);
            const Icon = appearance.Icon;
            const detail = getStepDetail(key, step);
            const isReady = key === 'start_authorized' && (step.status === 1 || step.status === 2);

            return (
              <div key={key} className={`flex-1 min-h-[84px] flex flex-col justify-center gap-2 rounded-md p-3 ${appearance.background} ${isReady ? 'bg-perception-success-600/40' : ''}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex-shrink-0 flex items-center justify-center size-9 rounded-md bg-perception-black-500 text-xl font-semibold ${appearance.color}">{index + 1}</span>
                  <Icon className={`size-8 flex-shrink-0 ${appearance.color} ${appearance === STEP_APPEARANCES.wait ? 'animate-spin' : ''}`} />
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className={`text-xl font-medium min-w-0 ${isReady ? 'text-perception-success-300' : 'text-white'}`}>{isReady ? 'Início autorizado - PRONTO PARA INICIAR' : title}</span>
                    {appearance !== STEP_APPEARANCES.idle && <Badge text={appearance.label} type={appearance.badgeType} />}
                  </div>
                  {detail != null && detail !== '' &&
                    <div className="flex flex-shrink-0 items-center justify-end gap-2 text-right text-lg text-perception-gray-500 max-w-[36%] break-words">
                      {key === 'rack_detection' && <span className={`size-3 flex-shrink-0 rounded-full ring-2 ${getRackColorDotClass(detail)}`} />}
                      <span>{key === 'rack_detection' ? capitalizeFirst(detail) : detail}</span>
                    </div>
                  }
                </div>
              </div>
            );
          })
        }
      </div>
    </div>
  );
});

export const DiorScanProcess = ({ scanState, processes }) => {
  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();
  const [scanProcess, setScanProcess] = useState({ steps: {}, positions: {}, tag_image: null });

  useEffect(() => {
    if (!socket) return;

    const handleScanProcess = (data) => {
      if (!data || typeof data !== 'object' || !data.scan_process || typeof data.scan_process !== 'object' || Array.isArray(data.scan_process)) return;
      setScanProcess((previous) => mergeScanProcess(previous, data.scan_process));
    };

    subscribeToEvent('current_data', handleScanProcess);
    return () => unsubscribeFromEvent('current_data', handleScanProcess);
  }, [socket]);

  const steps = scanProcess.steps;

  return (
    <Panel title="ESCANEAMENTO DO RACK" height="full" className="rounded-md [&>div:first-child>span]:text-lg" noPadding={true} overflowAuto={false}>
      <div className="relative flex h-full min-h-0 w-full">
        <div className="w-[55%] min-w-0 flex flex-col min-h-0">
          <DiorScanDigitalTwin steps={steps} positions={scanProcess?.positions} />
          <DiorScanFeedback scanState={scanState} steps={steps} processes={processes} tagImage={scanProcess?.tag_image} />
        </div>
        <DiorScanSteps steps={steps} />
        {
          steps?.start_authorized?.status === 2 &&
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-perception-black-900/85 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-5 px-8 text-center text-white">
              <ArrowPathIcon className="size-16 animate-spin text-perception-blue" />
              <div className="text-4xl font-semibold">Aguarde antes de escanear o próximo rack...</div>
            </div>
          </div>
        }
      </div>
    </Panel>
  );
};